import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import {
  Close as CloseIcon,

  Delete as DeleteIcon,
} from '@material-ui/icons';
import { useAppState } from '../state/AppProvider';
import { ChatHistoryList } from './ChatHistoryList';
import { ChatHistoryLoadingState, historyDeleteAll } from '../api';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  container: {
    width: 320,
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontWeight: 600,
    fontSize: '18px',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
  errorContainer: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
}));

export const ChatHistoryPanel: React.FC = () => {
  const classes = useStyles();
  const { state, dispatch } = useAppState();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearingError, setClearingError] = useState(false);

  const handleClose = () => {
    dispatch({ type: 'TOGGLE_CHAT_HISTORY' });
  };

  const handleClearAll = async () => {
    setClearing(true);
    setClearingError(false);

    try {
      const response = await historyDeleteAll(discoveryApi, fetchApi);
      if (!response.ok) {
        setClearingError(true);
      } else {
        dispatch({ type: 'DELETE_CHAT_HISTORY' });
        setClearAllDialogOpen(false);
      }
    } catch (error) {
      console.error('Error clearing all conversations:', error);
      setClearingError(true);
    } finally {
      setClearing(false);
    }
  };

  const renderContent = () => {
    if (
      state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading
    ) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="textSecondary">
            Loading chat history
          </Typography>
        </Box>
      );
    }

    if (
      state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail &&
      !state.isCosmosDBAvailable.cosmosDB
    ) {
      return (
        <Box className={classes.errorContainer}>
          <Typography variant="body1" gutterBottom>
            {state.isCosmosDBAvailable.status || 'Error loading chat history'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Chat history can't be saved at this time
          </Typography>
        </Box>
      );
    }

    if (
      state.chatHistoryLoadingState === ChatHistoryLoadingState.Success &&
      state.isCosmosDBAvailable.cosmosDB
    ) {
      return <ChatHistoryList />;
    }

    return null;
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          Chat history
        </Typography>
        <Box className={classes.headerActions}>
          <IconButton
            size="small"
            onClick={() => setClearAllDialogOpen(true)}
            title="Clear all chat history"
          >
            <DeleteIcon />
          </IconButton>
          <IconButton size="small" onClick={handleClose} title="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box className={classes.content}>{renderContent()}</Box>

      {/* Clear All Confirmation Dialog */}
      <Dialog
        open={clearAllDialogOpen}
        onClose={() => !clearing && setClearAllDialogOpen(false)}
      >
        <DialogTitle>
          {!clearingError
            ? 'Are you sure you want to clear all chat history?'
            : 'Error deleting all of chat history'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {!clearingError
              ? 'All chat history will be permanently removed.'
              : 'Please try again. If the problem persists, please contact the site administrator.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!clearingError && (
            <Button
              onClick={handleClearAll}
              color="secondary"
              disabled={clearing}
            >
              Clear All
            </Button>
          )}
          <Button
            onClick={() => {
              setClearAllDialogOpen(false);
              setTimeout(() => setClearingError(false), 500);
            }}
            disabled={clearing}
          >
            {!clearingError ? 'Cancel' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

